import { useState, useEffect, type FormEvent } from "react";
import { Link, useSearchParams, Navigate } from "react-router-dom";
import { applyPageSeo } from "../../lib/seo";
import { trackPasswordResetCompleted } from "../../lib/analytics";

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    return applyPageSeo({
      title: "Reset Password",
      description: "Set a new password for your React Email Builder account.",
      path: "/auth/reset-password",
      robots: "noindex,nofollow",
    });
  }, []);

  // If no token, redirect to forgot password
  if (!token) {
    return <Navigate to="/auth/forgot-password" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          newPassword,
          token,
        }),
      });

      if (response.ok) {
        trackPasswordResetCompleted();
        setSuccess(true);
      } else {
        const data = await response.json().catch(() => null) as { message?: string } | null;
        setError(data?.message ?? "Failed to reset password. The link may have expired.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-md border border-slate-200/80 bg-white/90 p-7 shadow-[0_24px_64px_rgba(15,23,42,0.08)] backdrop-blur">
        {success ? (
          <div className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-md bg-emerald-50">
              <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7 text-emerald-600" aria-hidden="true">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="mt-5 text-2xl font-semibold tracking-tight text-slate-950">
              Password reset successfully
            </h1>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <div className="mt-6">
              <Link
                to="/auth/login"
                className="inline-flex items-center justify-center gap-2 rounded bg-slate-950 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-600">
                Reset password
              </p>
              <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                Set a new password
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                Choose a strong password for your account.
              </p>
            </div>

            <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-slate-700">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={isSubmitting}
                  required
                  minLength={8}
                  className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
                />
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-slate-700">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={isSubmitting}
                  required
                  className="mt-1.5 block w-full rounded-md border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10"
                />
              </div>

              {error ? (
                <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" role="alert">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? "Resetting password..." : "Reset password"}
              </button>
            </form>
          </>
        )}
      </div>

      <p className="mt-6 text-center text-sm text-slate-500">
        <Link to="/auth/login" className="font-semibold text-sky-600 transition hover:text-sky-500">
          Back to sign in
        </Link>
      </p>
    </div>
  );
}